import React from 'react';
import {Table} from 'react-bootstrap';
import {DefaultModal, Loader} from '../../components';
import {ModalsStore, PriceAlertsStore} from '../../stores';

export default class AlertHistoryModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      alertData: {},
      alertId: null,
      isLoading: true
    };

    this.handleModalChange = this.handleModalChange.bind(this);
    this.handlePriceAlertsStoreChange = this.handlePriceAlertsStoreChange.bind(this);
  }

  componentDidMount() {
    ModalsStore.addChangeListener(this.handleModalChange);
    PriceAlertsStore.addChangeListener(this.handlePriceAlertsStoreChange);
  }

  componentWillUnmount() {
    ModalsStore.removeChangeListener(this.handleModalChange);
    PriceAlertsStore.removeChangeListener(this.handlePriceAlertsStoreChange);
  }

  /**
   * Denormalize alert data by adding related entities (history, supplier, platt)
   * @param {object} alertData - Alert data with entity IDs
   * @param {object} entitiesJS - Plain JS object of entities
   * @returns {object} Alert data with denormalized entities
   */
  denormalizeAlertData(alertData, entitiesJS) {
    // Add history if it exists
    if (alertData.history && entitiesJS.priceAlertHistory) {
      alertData.history = alertData.history.map(historyId => {
        const historyRecord = entitiesJS.priceAlertHistory[historyId];
        // Add user info if exists
        if (historyRecord && historyRecord.userId && entitiesJS.users) {
          historyRecord.user = entitiesJS.users[historyRecord.userId];
        }
        return historyRecord;
      }).filter(h => h); // Filter out undefined records
    } else {
      alertData.history = [];
    }
    
    // Add supplier if exists
    if (alertData.supplierId && entitiesJS.suppliers) {
      alertData.Supplier = entitiesJS.suppliers[alertData.supplierId];
    }
    
    // Add platt if exists
    if (alertData.plattId && entitiesJS.platts) {
      alertData.Platt = entitiesJS.platts[alertData.plattId];
    }
    
    return alertData;
  }

  /**
   * Format user name for display
   * @param {object} user - User object
   * @returns {string} Formatted user name
   */
  getUserName(user) {
    if (!user) return 'System';
    return user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
  }

  handleModalChange() {
    if (ModalsStore.isModalVisible(this.props.id)) {
      const payload = ModalsStore.getPayload(this.props.id);
      // Convert Immutable Map to plain object if needed
      const alertData = payload && payload.toJS ? payload.toJS() : payload;
      const alertId = alertData ? alertData.id : null;
      
      // Try to get the full alert data from store immediately
      if (alertId) {
        const alerts = PriceAlertsStore.get('priceAlerts');
        const entities = PriceAlertsStore.get('entities');
        
        if (alerts && alerts.has(String(alertId))) {
          const alert = alerts.get(String(alertId));
          const fullAlertData = alert.toJS();
          
          // Check if ALL history entities are actually available
          let hasValidHistoryData = false;
          
          if (fullAlertData.history && fullAlertData.history.length > 0 && entities) {
            const entitiesJS = entities.toJS();
            
            // Check if ALL history records are populated in entities
            if (entitiesJS.priceAlertHistory) {
              hasValidHistoryData = fullAlertData.history.every(historyId => 
                entitiesJS.priceAlertHistory[historyId]
              );
            }
            
            // Only proceed to denormalize if we have valid history data
            if (hasValidHistoryData) {
              this.denormalizeAlertData(fullAlertData, entitiesJS);
              
              // Data is ready, show it
              this.setState({
                alertData: fullAlertData,
                alertId: alertId,
                isLoading: false
              });
              return;
            }
          }
        }
      }
      
      // If we couldn't get valid data from store, set loading state
      this.setState({
        alertData: alertData || {},
        alertId: alertId,
        isLoading: true
      });
    }
  }

  handlePriceAlertsStoreChange() {
    // If we have an alert ID and modal is visible, update with latest data from store
    if (this.state.alertId && ModalsStore.isModalVisible(this.props.id)) {
      const alerts = PriceAlertsStore.get('priceAlerts');
      const entities = PriceAlertsStore.get('entities');
      
      if (alerts && alerts.has(String(this.state.alertId))) {
        const alert = alerts.get(String(this.state.alertId));
        const alertData = alert.toJS();
        
        // Denormalize related entities
        if (entities) {
          const entitiesJS = entities.toJS();
          this.denormalizeAlertData(alertData, entitiesJS);
        }
        
        this.setState({ 
          alertData,
          isLoading: false // Data is loaded
        });
      }
    }
  }

  formatDate(dateString) {
    if (!dateString) return 'N/A';
    const m = require('moment')(dateString);
    if (!m.isValid()) return 'N/A';
    return m.format('YYYY-MM-DD');
  }

  formatAction(action) {
    const actionMap = {
      'created': 'Created',
      'skipped': 'Skipped',
      'resolved': 'Resolved',
      'updated': 'Updated',
      'invalidated': 'Invalidated',
      'reactivated': 'Reactivated'
    };
    return actionMap[action] || action;
  }

  getActionBadge(action) {
    const badgeMap = {
      'created': 'label-default',
      'skipped': 'label-info',
      'resolved': 'label-success',
      'updated': 'label-warning',
      'invalidated': 'label-danger',
      'reactivated': 'label-primary'
    };
    return badgeMap[action] || 'label-default';
  }

  render() {
    const {alertData, isLoading} = this.state;

    // Show loader while data is loading
    if (isLoading || !alertData || Object.keys(alertData).length === 0) {
      return (
        <DefaultModal
          id={this.props.id}
          title={this.props.title}
          onHide={this.props.onHide}
          bsSize="large">
          <Loader>Loading alert history...</Loader>
        </DefaultModal>
      );
    }

    const history = alertData.history || [];

    return (
      <DefaultModal
        id={this.props.id}
        title={this.props.title}
        onHide={this.props.onHide}
        bsSize="large">
        <div>
          <div className="row" style={{marginBottom: '15px'}}>
            <div className="col-md-6">
              {!alertData.isPublic && alertData.Platt && (alertData.Platt.code || alertData.Platt.name) ? (
                <span><strong>Platt: </strong>{[alertData.Platt.code, alertData.Platt.name].filter(Boolean).join(' - ') || 'N/A'}</span>
              ) : alertData.isPublic ? (
                <span><strong>Airport: </strong>{alertData.airportIcao || 'N/A'}</span>
              ) : !alertData.isPublic ? (
                <span><strong>Platt: </strong>N/A</span>
              ) : null}
            </div>
            <div className="col-md-6">
              <strong>Supplier: </strong>{alertData.Supplier ? alertData.Supplier.name : 'N/A'}
            </div>
          </div>

          {history.length === 0 ? (
            <div className="alert alert-info">
              No history records found for this alert.
            </div>
          ) : (
            <div style={{maxHeight: '400px', overflowY: 'auto', overflowX: 'auto'}}>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Date & Time</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((record, index) => {
                    // Skip rendering if record is incomplete
                    if (!record || !record.createdAt) return null;
                    
                    return (
                      <tr key={index}>
                        <td>{this.formatDate(record.createdAt)}</td>
                        <td>{this.getUserName(record.user)}</td>
                        <td>
                          <span className={`label ${this.getActionBadge(record.action)}`}>
                            {this.formatAction(record.action)}
                          </span>
                        </td>
                        <td>
                          {this.renderDetails(record)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </DefaultModal>
    );
  }

  renderDetails(record) {
    if (!record) return <em>No details</em>;
    
    const details = [];

    // Only show status if both old and new status are defined
    if (record.oldStatus && record.newStatus) {
      details.push(
        <div key="status">
          <strong>Status: </strong>{record.oldStatus} → {record.newStatus}
        </div>
      );
    } else if (record.newStatus) {
      // If only new status exists (e.g., for 'created' action)
      details.push(
        <div key="status">
          <strong>Status: </strong>{record.newStatus}
        </div>
      );
    }

    if (record.oldValue !== null && record.oldValue !== undefined && 
        record.newValue !== null && record.newValue !== undefined) {
      // Check if this is a baseline price change
      const isBaselineChange = record.notes && record.notes.toLowerCase().includes('baseline');
      const priceLabel = isBaselineChange ? 'Baseline Price: ' : 'Price: ';
      
      details.push(
        <div key="value">
          <strong>{priceLabel}</strong>{parseFloat(record.oldValue).toFixed(5)} → {parseFloat(record.newValue).toFixed(5)}
        </div>
      );
    }

    if (record.oldPercentageChange !== null && record.oldPercentageChange !== undefined && 
        record.newPercentageChange !== null && record.newPercentageChange !== undefined) {
      details.push(
        <div key="percentage">
          <strong>% Change: </strong>{parseFloat(record.oldPercentageChange).toFixed(2)}% → {parseFloat(record.newPercentageChange).toFixed(2)}%
        </div>
      );
    }

    if (record.notes) {
      details.push(
        <div key="notes">
          <em>{record.notes}</em>
        </div>
      );
    }

    return details.length > 0 ? details : <em>No details</em>;
  }
}
